import Card from '../../components/ui/Card';
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { tourismApi, type TourismDimension } from '../../api/tourism';
import {
  CHART,
  formatDate,
  formatDayLong,
  formatNumber,
  formatPercent,
} from '../../config/chartTheme';
import { useTourismFilter } from '../../hooks/useTourismFilter';
import BreakdownChart, { DIMENSION_LABELS } from '../../components/tourism/BreakdownChart';
import ChartCard from '../../components/tourism/ChartCard';
import CoverageStrip from '../../components/tourism/CoverageStrip';
import DataBasisFootnote from '../../components/tourism/DataBasisFootnote';
import OccupancyCalendar from '../../components/tourism/OccupancyCalendar';
import OccupancyTimeline, { type BrushRange } from '../../components/tourism/OccupancyTimeline';
import SignalTimeline from '../../components/tourism/SignalTimeline';
import StatTile from '../../components/tourism/StatTile';
import TourismFilterBar from '../../components/tourism/TourismFilterBar';
import { buildReferenceRows, buildTimeRows, type TimeRow } from '../../components/tourism/timeRows';

/** Grenzt einen getippten Datumswert auf das erlaubte Fenster ein. `min`/`max`
 *  an `<input type="date">` verhindern nur die Auswahl im Picker, nicht die
 *  Tastatureingabe. */
function clampDate(value: string, min?: string | null, max?: string | null): string {
  if (!value) return value;
  if (min && value < min) return min;
  if (max && value > max) return max;
  return value;
}

export default function Daten() {
  const { data: meta } = useQuery({
    queryKey: ['tourism', 'meta'],
    queryFn: tourismApi.getMeta,
    staleTime: 10 * 60 * 1000,
  });

  const gruppeNames = useMemo(
    () => new Map((meta?.options.gruppen ?? []).map(g => [g.id, g.name])),
    [meta],
  );
  const filterApi = useTourismFilter(gruppeNames);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dimension, setDimension] = useState<TourismDimension>('neighborhood');

  const params = useMemo(
    () => ({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      dimension,
      ...filterApi.params,
    }),
    [dateFrom, dateTo, dimension, filterApi.params],
  );

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['tourism', 'overview', params],
    queryFn: () => tourismApi.getOverview(params),
    // Beim Filtern die vorhandene Darstellung halten statt Skelett – kein
    // Layout-Sprung, die Kurve bleibt vergleichbar.
    placeholderData: keepPreviousData,
  });

  const today = data?.today ?? '';
  const cityRows = useMemo(
    () => buildReferenceRows(data?.series ?? [], today),
    [data, today],
  );
  const selectionRows = useMemo(
    () => buildTimeRows(data?.series ?? [], today),
    [data, today],
  );

  // Zoom-Bereich der Brush-Leiste. Er begrenzt die Tabellenansicht — sonst
  // stünden bis zu 730 Zeilen je Karte im DOM.
  const [brush, setBrush] = useState<BrushRange | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Data length changes invalidate the previous chart selection.
    setBrush(null);
  }, [cityRows.length]);

  const visible = <T,>(rows: T[]): T[] =>
    brush ? rows.slice(brush.startIndex, brush.endIndex + 1) : rows;
  const rangeLabel = (rows: TimeRow[]): string | undefined => {
    const slice = visible(rows);
    if (!brush || slice.length === 0) return undefined;
    return `${formatDayLong(slice[0].iso)} – ${formatDayLong(slice[slice.length - 1].iso)}`;
  };

  const selection = data?.selection_kpis;
  const reference = data?.reference_kpis;
  const filtersActive = data?.filters_active ?? false;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* ─── Kopf ─────────────────────────────────────────────────────── */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ferienwohnungsmarkt im Stadtgebiet</h1>
            <p className="text-sm text-gray-500 mt-1">
              Verfügbarkeit und Belegung als Nachfrage-Indikator – Vergangenheit, Gegenwart und was
              die Plattform für die kommenden Monate ausweist.
            </p>
          </div>
          <div className="text-xs text-gray-400 text-right">
            Datenstand {formatDate(meta?.last_scrape_at?.slice(0, 10))}
          </div>
        </div>

        {/* ─── Zeitraum ─────────────────────────────────────────────────── */}
        <Card className="p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="daten-von">
              Von
            </label>
            <input
              id="daten-von"
              type="date"
              value={dateFrom}
              min={meta?.data_start ?? undefined}
              max={meta?.horizon_end ?? undefined}
              onChange={e => setDateFrom(clampDate(e.target.value, meta?.data_start, meta?.horizon_end))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="daten-bis">
              Bis
            </label>
            <input
              id="daten-bis"
              type="date"
              value={dateTo}
              min={dateFrom || meta?.data_start || undefined}
              max={meta?.horizon_end ?? undefined}
              onChange={e => setDateTo(clampDate(e.target.value, meta?.data_start, meta?.horizon_end))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            />
          </div>
          {meta?.data_start && (
            <p className="text-xs text-gray-400 pb-2">
              Daten liegen ab {formatDate(meta.data_start)} vor.
            </p>
          )}
        </Card>

        {isError ? (
          <Card className="p-8 text-center text-red-600">
            Daten konnten nicht geladen werden.
          </Card>
        ) : isLoading || !data ? (
          <Card className="p-8 text-center text-gray-500">
            Wird berechnet…
          </Card>
        ) : (
          <>
            {/* ─── Zone 1: Gesamtstadt ──────────────────────────────────── */}
            <section className="space-y-4">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Gesamtstadt
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatTile
                  label="Inserate im Bestand"
                  value={formatNumber(reference?.listings_matched)}
                  sub={`${formatNumber(reference?.listings_with_data)} mit Verfügbarkeitsdaten`}
                />
                <StatTile
                  label="Ø Belegung im Zeitraum"
                  value={formatPercent(reference?.occupancy)}
                  accent={CHART.occupancy}
                  sub={`${formatNumber(reference?.known_days)} bekannte Tage`}
                />
                <StatTile
                  label="Buchungs-Signale"
                  value={formatNumber(reference?.booking_signals)}
                  accent={CHART.signal}
                  sub="Indizien, keine bestätigten Buchungen"
                />
                <StatTile
                  label="Erhebungen"
                  value={formatNumber(meta?.scrape_days.length)}
                  sub={`seit ${formatDate(meta?.data_start)}`}
                />
              </div>

              <ChartCard
                title="Belegung im Zeitverlauf"
                hint="Ein Punkt je Tag. Anteil der nicht verfügbaren Tage an den Tagen, für die die Plattform eine Angabe macht. Gestrichelt ab heute: angekündigte Verfügbarkeit, keine Prognose."
                dimmed={isFetching}
                caption={rangeLabel(cityRows)}
                table={{
                  headers: ['Tag', 'Phase', 'Belegung', 'Inserate', 'Signale'],
                  rows: visible(cityRows).map(r => [
                    r.iso,
                    r.isFuture ? 'angekündigt' : 'beobachtet',
                    r.observedPct == null && r.announcedPct == null
                      ? '–'
                      : `${String(r.observedPct ?? r.announcedPct).replace('.', ',')} %`,
                    formatNumber(r.n ?? undefined),
                    formatNumber((r.signalsPast ?? r.signalsFuture) ?? undefined),
                  ]),
                }}
              >
                <OccupancyTimeline rows={cityRows} showReference={false} />
                <div className="mt-2">
                  <CoverageStrip
                    rows={cityRows}
                    source="reference"
                    total={reference?.listings_matched ?? 0}
                  />
                </div>
              </ChartCard>

              <ChartCard
                title="Buchungs-Signale je Tag"
                hint="Ein Signal ist ein Übergang frei → belegt zwischen zwei Erhebungen. Eigene Skala, deshalb ein eigenes Diagramm. Ziehen Sie unten den Ausschnitt — alle Diagramme folgen."
                dimmed={isFetching}
              >
                {/* Die Zoom-Leiste steht nur an diesem, dem untersten
                    Zeitdiagramm; über `syncId` folgen die anderen. */}
                <SignalTimeline rows={cityRows} withBrush onBrushChange={setBrush} />
              </ChartCard>

              <ChartCard
                title="Belegungskalender"
                hint="Dieselben Tageswerte als Monatsraster. Feiertagsblöcke wie die Silvesterwoche sind hier als zusammenhängende Fläche erkennbar."
                dimmed={isFetching}
              >
                <OccupancyCalendar rows={visible(cityRows)} />
              </ChartCard>
            </section>

            {/* ─── Zone 2: Auswahl ──────────────────────────────────────── */}
            <section className="space-y-4 pt-2 border-t-2 border-gray-200">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 pt-4">
                Ihre Auswahl
              </h2>

              <TourismFilterBar
                api={filterApi}
                meta={meta}
                matched={selection?.listings_matched ?? 0}
                total={reference?.listings_matched ?? 0}
              />

              <ChartCard
                title={
                  filtersActive
                    ? 'Belegung: Auswahl im Vergleich zur Gesamtstadt'
                    : 'Belegung der Auswahl'
                }
                hint={
                  filtersActive
                    ? 'Die graue Fläche ist die Gesamtstadt als Bezugsgröße.'
                    : 'Noch kein Filter gesetzt – die Auswahl entspricht der Gesamtstadt. Setzen Sie oben einen Filter, um zu vergleichen.'
                }
                dimmed={isFetching}
                caption={rangeLabel(selectionRows)}
                table={{
                  headers: ['Tag', 'Belegung Auswahl', 'Belegung Stadt', 'Inserate'],
                  rows: visible(selectionRows).map(r => {
                    const own = r.observedPct ?? r.announcedPct;
                    return [
                      r.iso,
                      own == null ? '–' : `${String(own).replace('.', ',')} %`,
                      r.referencePct == null ? '–' : `${String(r.referencePct).replace('.', ',')} %`,
                      formatNumber(r.n ?? undefined),
                    ];
                  }),
                }}
              >
                <OccupancyTimeline rows={selectionRows} showReference={filtersActive} />
                <div className="mt-2">
                  <CoverageStrip
                    rows={selectionRows}
                    total={selection?.listings_matched ?? 0}
                  />
                </div>
              </ChartCard>

              <ChartCard
                title="Aufschlüsselung"
                hint="Belegung im gewählten Zeitraum, gruppiert nach einem Merkmal der Auswahl."
                dimmed={isFetching}
                right={
                  <label className="flex items-center gap-2 text-xs text-gray-500">
                    Nach
                    <select
                      value={dimension}
                      onChange={e => setDimension(e.target.value as TourismDimension)}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                    >
                      {(meta?.dimensions ?? []).map(d => (
                        <option key={d} value={d}>
                          {DIMENSION_LABELS[d] ?? d}
                        </option>
                      ))}
                    </select>
                  </label>
                }
                table={{
                  headers: [DIMENSION_LABELS[dimension] ?? 'Merkmal', 'Belegung', 'Inserate', 'Signale'],
                  rows: [
                    ...data.breakdown.map(b => [
                      b.key,
                      formatPercent(b.occupancy, 1),
                      formatNumber(b.listings),
                      formatNumber(b.booking_signals),
                    ]),
                    ...(data.unknown
                      ? [[
                          'Merkmal unbekannt',
                          formatPercent(data.unknown.occupancy, 1),
                          formatNumber(data.unknown.listings),
                          formatNumber(data.unknown.booking_signals),
                        ]]
                      : []),
                  ],
                }}
              >
                <BreakdownChart
                  buckets={data.breakdown}
                  referencePct={
                    reference?.occupancy == null
                      ? null
                      : Math.round(reference.occupancy * 1000) / 10
                  }
                />
                {data.unknown && (
                  // Keine eigene Säule: "unbekannt" ist kein Segment des Marktes,
                  // sondern eine Lücke in den Daten.
                  <p className="text-xs text-gray-400 mt-2">
                    Merkmal für {data.unknown.listings}{' '}
                    {data.unknown.listings === 1 ? 'Inserat' : 'Inserate'} unbekannt – nicht in der
                    Aufschlüsselung enthalten.
                  </p>
                )}
              </ChartCard>
            </section>

            <DataBasisFootnote meta={meta} />
          </>
        )}
      </main>
    </div>
  );
}
